import { useCallback } from 'react';
import { 
  DEFAULT_INCLUDED_SERVICES, 
  PROCESS_TEMPLATES, 
  WORKFLOW_BADGE_MAP 
} from '../constants/marketing-display.constants';
import { getDurationLabel } from '../utils/capacity-brackets.utils';

export function useDisplaySync() {
  const syncDisplay = useCallback((formData: any) => {
    // 1. Map Brackets to TarifGroups
    const tarifGroups = [
      {
        title: 'Tarifs & Durée (Hors Taxes)',
        description: 'Tarification selon la capacité et la durée',
        order: 1,
        tarifs: (formData.capacityBrackets || []).map((bracket: any, idx: number) => {
          // Trouver le prix principal (souvent FULL_DAY ou le premier disponible)
          const priceObj = bracket.prices?.find((p: any) => p.durationType === 'FULL_DAY') || bracket.prices?.[0];
          return {
            label: bracket.label,
            price: priceObj?.price || 0,
            unit: 'FCFA',
            detail: bracket.pricingUnit === 'per_person' ? 'Par personne' : 'Forfait groupe',
            note: null,
            order: idx + 1,
          };
        }),
      },
    ];

    // 2. Map Free Equipments and Default Services to Inclus + Paid Equipments to Options
    const services = [
      ...DEFAULT_INCLUDED_SERVICES,
      ...(formData.equipments || []).map((eq: any, idx: number) => ({
        iconName: eq.iconName || 'Package',
        label: eq.name,
        description: eq.description || '',
        extraPrice: eq.price > 0 ? `+${eq.price.toLocaleString()} ${eq.unit}` : null,
        order: DEFAULT_INCLUDED_SERVICES.length + idx + 1,
      })),
    ];

    // 3. Map Workflow to ProcessSteps
    const workflow = formData.workflow || 'IMMEDIATE';
    const processSteps = (PROCESS_TEMPLATES as any)[workflow] || PROCESS_TEMPLATES.IMMEDIATE;

    // 4. Generate InfoBadges
    const infoBadges = [];
    
    // Badge 1: Workflow
    const workflowBadge = (WORKFLOW_BADGE_MAP as any)[workflow];
    if (workflowBadge) {
      infoBadges.push({
        iconName: workflowBadge.iconName,
        title: workflowBadge.label,
        subtitle: workflow === 'IMMEDIATE' ? 'Confirmation immédiate' : 'Réponse sous 48h',
        order: 1,
      });
    }

    // Badge 2: Flexibilité
    if (formData.allowHalfDay || formData.allowMultiDay) {
      infoBadges.push({
        iconName: 'Clock',
        title: 'Flexibilité',
        subtitle: 'Demi-journée ou multi-jours',
        order: 2,
      });
    }

    // Badge 3: Inclus
    infoBadges.push({
      iconName: 'Sparkles',
      title: 'Tout inclus',
      subtitle: 'WiFi, café et services de base',
      order: 3,
    });

    // 5. Calculate "Price From"
    // Le client attend que priceFromLabel SOIT le prix formaté (ex: "5 000 FCFA")
    let minPrice = Infinity;
    let minDurationLabel = 'demi-journée';
    
    (formData.capacityBrackets || []).forEach((bracket: any) => {
      (bracket.prices || []).forEach((p: any) => {
        if (p.price > 0 && p.price < minPrice) {
          minPrice = p.price;
          minDurationLabel = getDurationLabel(p.durationType).toLowerCase();
        }
      });
    });

    if (minPrice === Infinity) minPrice = 0;

    return {
      tarifGroups,
      services,
      processSteps,
      infoBadges,
      // On stocke le prix formaté dans priceFromLabel car c'est ce que le client affiche
      priceFromLabel: minPrice > 0 ? `${minPrice.toLocaleString()} FCFA` : 'Sur devis',
      priceFromDetail: `Hors Taxes / ${minDurationLabel}`,
    };
  }, []);

  return { syncDisplay };
}
